import { useEffect, useMemo, useState } from 'react';
import {
  EmbeddedCheckout,
  EmbeddedCheckoutProvider
} from '@stripe/react-stripe-js';
import { confirmCardUpdate, createBillingSession } from '@api-service/account';
import FFHModal from '@components/modal';
import { triggerToast } from '@components/atoms/toast';
import { BodyRegular } from '@components/atoms/typography';
import { datadogRumService } from '@lib/datadogRum';
import { getStripe } from '@lib/stripe';

type BillingSession = {
  clientSecret: string;
  sessionId: string;
};

type UpdateCardModalProps = {
  isOpen: boolean;
  accessToken: string | null;
  onClose: () => void;
  onCardUpdated: () => void;
};

const UpdateCardModal = ({
  isOpen,
  accessToken,
  onClose,
  onCardUpdated
}: UpdateCardModalProps) => {
  const [session, setSession] = useState<BillingSession | null>(null);

  // loadStripe is memoised inside getStripe(); useMemo keeps the same
  // promise across re-renders so the embedded provider does not remount.
  const stripePromise = useMemo(() => getStripe(), []);

  useEffect(() => {
    if (!isOpen || !accessToken || session) return;

    const fetchSession = async () => {
      let billingSession: BillingSession | { error: true };
      try {
        billingSession = await createBillingSession({ accessToken });
        if ('error' in billingSession) {
          // An error response from the API, as opposed to the network-level
          // failure the catch below reports with its original error (FFH-1558)
          datadogRumService.addError(
            new Error('Update card billing session creation failed'),
            { context: 'update-card-modal', stage: 'create_session' }
          );
        }
      } catch (error) {
        billingSession = { error: true };
        datadogRumService.addError(error, {
          context: 'update-card-modal',
          stage: 'create_session'
        });
      }

      if ('error' in billingSession) {
        triggerToast({
          title: 'Error',
          subTitle: 'Failed to start the card update. Please contact support',
          variant: 'error'
        });
        onClose();
        return;
      }

      setSession(billingSession);
    };

    fetchSession();
  }, [isOpen, accessToken, session]);

  // A checkout session is single-use: once completed or abandoned it cannot
  // be remounted, so closing discards it and reopening fetches a fresh one.
  const handleClose = () => {
    setSession(null);
    onClose();
  };

  const handleComplete = async () => {
    if (!accessToken || !session) return;

    let isSuccess = false;
    try {
      const confirmation = await confirmCardUpdate({
        accessToken,
        sessionId: session.sessionId
      });
      isSuccess = confirmation.isSuccess;
      if (!isSuccess) {
        datadogRumService.addError(
          new Error('Update card confirmation failed'),
          {
            context: 'update-card-modal',
            stage: 'confirm_session',
            sessionId: session.sessionId
          }
        );
      }
    } catch (error) {
      // The card is already saved with Stripe at this point; a failure here
      // means the subscription's default payment method was not switched
      datadogRumService.addError(error, {
        context: 'update-card-modal',
        stage: 'confirm_session',
        sessionId: session.sessionId
      });
    }

    triggerToast(
      isSuccess
        ? {
            title: 'Success',
            subTitle: 'Payment details successfully updated',
            variant: 'success'
          }
        : {
            title: 'Error',
            subTitle: 'Payment details not updated',
            variant: 'error'
          }
    );

    if (isSuccess) {
      onCardUpdated();
    }
    handleClose();
  };

  return (
    <FFHModal open={isOpen} handleClose={handleClose} title="Update card">
      <div className="p-6 pt-10 w-[92vw] max-w-[480px]">
        {session ? (
          <EmbeddedCheckoutProvider
            stripe={stripePromise}
            options={{
              clientSecret: session.clientSecret,
              onComplete: handleComplete
            }}
          >
            <EmbeddedCheckout />
          </EmbeddedCheckoutProvider>
        ) : (
          <BodyRegular className="text-center py-10">Loading...</BodyRegular>
        )}
      </div>
    </FFHModal>
  );
};

export default UpdateCardModal;
